import { useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { motion } from 'framer-motion'
import { ChevronRightIcon, TagIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../contexts/AuthContext'
import FormPageSkeleton from '../components/common/FormPageSkeleton'
import ErrorMessage from '../components/common/ErrorMessage'
import SaleForm from '../components/sales/SaleForm'
import { api } from '../services/api'
import { trackEngagement, trackConversion } from '../utils/analytics'
import { idFromSlug, eventPath, newSalePath } from '../utils/slugs'

export default function NewSalePage() {
  usePageTitle('Submit Your Garage Sale')
  const navigate = useNavigate()
  const { id: eventSlug } = useParams()
  const eventId = idFromSlug(eventSlug)
  const { user } = useAuth()
  const { execute: submitSale, loading, error } = useApi()
  const { data: event, loading: eventLoading, error: eventError, execute: fetchEvent } = useApi()

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: `/events/${eventSlug}/sales/new` } })
    }
  }, [user, navigate, eventId])

  useEffect(() => {
    if (!eventId) {
      navigate('/events')
      return
    }
    fetchEvent(() => api.events.get(eventId))
  }, [eventId, navigate])

  const handleSubmit = async (formData) => {
    try {
      const response = await submitSale(() => api.sales.create(formData))
      trackEngagement.saleCreated(eventId)
      if (response?.id) {
        trackConversion.salePublished(response.id, eventId)
      }
      navigate(event ? eventPath(event) : '/events')
    } catch (error) {
      console.error('Error submitting sale:', error)
    }
  }

  if (eventLoading) {
    return <FormPageSkeleton />
  }

  if (eventError) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <ErrorMessage
          message={eventError.message}
          onRetry={() => fetchEvent(() => api.events.get(eventId))}
        />
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
    >
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-text-muted mb-6 overflow-x-auto">
        <Link to="/events" className="link-subtle hover:text-text whitespace-nowrap">Events</Link>
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
        {event && (
          <>
            <Link to={eventPath(event)} className="link-subtle hover:text-text truncate max-w-[150px]">
              {event.name}
            </Link>
            <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
          </>
        )}
        <span className="text-text font-medium whitespace-nowrap">New Sale</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start gap-3.5 mb-8">
        <div className="rounded-xl bg-primary-50 dark:bg-primary-50 p-2.5 flex-shrink-0
          ring-4 ring-primary-50/50 dark:ring-primary-50/30">
          <TagIcon className="h-6 w-6 text-primary-600 dark:text-primary-500" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text leading-tight">Submit Your Sale</h1>
          <p className="text-text-muted mt-1 text-sm sm:text-base">
            {event ? `Add your sale to "${event.name}"` : 'Fill in the details below'}
          </p>
        </div>
      </div>

      {/* Helpful hint for first-time users */}
      {event && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.25 }}
          className="mb-6 flex items-start gap-2.5 rounded-xl
            bg-info-50 dark:bg-info-50 border border-info-200 dark:border-info-200
            p-3.5 text-sm text-info-700 dark:text-info-600"
        >
          <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            Your sale must be within <strong>{event.radius} miles</strong> of the event center.
            Use the map below to set your sale location.
          </p>
        </motion.div>
      )}

      <SaleForm
        mode="create"
        event={event}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        submitButtonText="Submit Sale"
        loadingText="Submitting..."
      />
    </motion.div>
  )
}
